// Function to update podium based on the (already) sorted table
const updatePodium = (block, table) => {
  const podium = block.querySelector('.container__podium')
  if (!podium) return

  const tbody = table.querySelector('tbody')
  const rows = Array.from(tbody.querySelectorAll('tr'))
  const topThree = rows.slice(0, 3)

  const podiumPlayers = Array.from(podium.querySelectorAll('.podium__player'))

  // remove previous rank classes only
  podiumPlayers.forEach((p) => {
    Array.from(p.classList).forEach((c) => {
      if (c.startsWith('podium__player--rank-')) p.classList.remove(c)
    })
  })

  topThree.forEach((row, index) => {
    const podiumPlayer = podiumPlayers[index]
    if (!podiumPlayer) return

    const playerImage = row.querySelector('.player-infos__image img')
    const podiumImage = podiumPlayer.querySelector('.infos__image img')
    if (playerImage && podiumImage) {
      podiumImage.replaceWith(playerImage.cloneNode(true))
    }

    const name = row.querySelector('.player-infos__name')?.textContent?.trim() || ''
    const podiumName = podiumPlayer.querySelector('.infos__name')
    if (podiumName) podiumName.textContent = name

    podiumPlayer.classList.add(`podium__player--rank-${index + 1}`)
    const podiumRanking = podiumPlayer.querySelector('.infos__ranking')
    if (podiumRanking) podiumRanking.textContent = String(index + 1)
  })
}

// Function to update row positions based on score (DESC) then votes (DESC)
const updateRowPositions = (table) => {
  const tbody = table.querySelector('tbody')
  if (!tbody) return

  const rows = Array.from(tbody.querySelectorAll('tr'))

  const safeInt = (v) => {
    const s = String(v ?? '')
    const m = s.match(/-?\d+/)
    return m ? parseInt(m[0], 10) : 0
  }
  const getInt = (row, sel) => {
    const el = row.querySelector(sel)
    return el ? safeInt(el.textContent) : 0
  }

  // Stable sort fallback: remember current order
  const currentOrder = new Map(rows.map((r, i) => [r, i]))

  rows.sort((a, b) => {
    const scoreA = getInt(a, '.score-vote-wrapper__score')
    const scoreB = getInt(b, '.score-vote-wrapper__score')
    if (scoreB !== scoreA) return scoreB - scoreA

    const votesA = getInt(a, '.js-vote-count')
    const votesB = getInt(b, '.js-vote-count')
    if (votesB !== votesA) return votesB - votesA

    // tie-breaker: keep current visual order
    return currentOrder.get(a) - currentOrder.get(b)
  })

  // Reset arrows/animations
  rows.forEach((row) => {
    const icon = row.querySelector('.js-player-rank-icon')
    if (icon) {
      if (icon.dataset.iconUp) icon.classList.remove(icon.dataset.iconUp)
      if (icon.dataset.iconDown) icon.classList.remove(icon.dataset.iconDown)
    }
    row.classList.remove('moving', 'moving-up', 'moving-down')
  })

  // Apply order + animate + renumber
  rows.forEach((row, idx) => {
    const prev = safeInt(row.dataset.position)
    const next = idx + 1

    if (prev && prev !== next) {
      row.classList.add('moving', prev > next ? 'moving-up' : 'moving-down')

      const icon = row.querySelector('.js-player-rank-icon')
      if (icon) {
        if (prev > next && icon.dataset.iconUp) {
          icon.classList.add(icon.dataset.iconUp)
          if (icon.dataset.iconDown) icon.classList.remove(icon.dataset.iconDown)
        } else if (prev < next && icon.dataset.iconDown) {
          icon.classList.add(icon.dataset.iconDown)
          if (icon.dataset.iconUp) icon.classList.remove(icon.dataset.iconUp)
        }
      }
    }

    const rankCell = row.querySelector('.js-player-rank')
    if (rankCell) rankCell.textContent = String(next)

    row.dataset.position = String(next)
    tbody.appendChild(row) // move in DOM
  })

  setTimeout(() => {
    rows.forEach((row) => row.classList.remove('moving', 'moving-up', 'moving-down'))
  }, 300)
}

// Function to fix vote wrapper positioning on mobile browsers
// Needed for IOS Safari/Chrome
const fixVoteWrapperPositioning = (table) => {
  const tbody = table.querySelector('tbody')
  if (!tbody) return

  // Get all rows in the table
  const rows = Array.from(tbody.querySelectorAll('tr'))

  rows.forEach((row) => {
    // Find the vote wrapper in this row
    const voteWrapper = row.querySelector('.vote__cta-wrapper')
    if (!voteWrapper) return

    // Apply the position to the vote wrapper
    voteWrapper.style.width = `${row.offsetWidth}px`
    voteWrapper.style.height = `${row.offsetHeight}px`

    // Add a class to indicate this has been positioned
    voteWrapper.classList.add('js-positioned')
  })
}

// Handle "See full ranking" button
const toggleSeeMore = (block, table) => {
  const seeMoreBtn = block.querySelector('.js-see-more-btn')
  if (seeMoreBtn) {
    seeMoreBtn.addEventListener('click', (e) => {
      e.preventDefault()
      const hiddenRows = table.querySelectorAll('tr[data-hidden]')
      if (!hiddenRows.length) return

      const isExpanded = seeMoreBtn.getAttribute('aria-expanded') === 'true'

      // Toggle visibility
      hiddenRows.forEach((row) => {
        row.dataset.hidden = isExpanded
      })

      // Update button state
      seeMoreBtn.setAttribute('aria-expanded', !isExpanded)
      seeMoreBtn.textContent = isExpanded ? seeMoreBtn.dataset.textCollapsed : seeMoreBtn.dataset.textExpanded
    })
  }
}

// Check if the user has already voted
const localStorageHasVoted = (postID) => {
  const storage = localStorage.getItem(postID)
  if (!storage) return false

  const parsedStorage = JSON.parse(storage)
  if (!parsedStorage.blockBestPlayerVoted) return false

  return true
}

export default function blockBestPlayer() {
  const blocks = document.querySelectorAll('.block---best-player')
  if (!blocks.length) return

  blocks.forEach((block) => {
    const postID = block.dataset.postId
    const hasVoted = localStorageHasVoted(postID)

    const table = block.querySelector('.container__table')
    if (!table) return

    toggleSeeMore(block, table)

    // Check if the browser width is below 576px
    const isMobile = window.matchMedia('(max-width: 575.98px)').matches

    if (isMobile) {
      // Fix vote wrapper positioning on initial load
      setTimeout(() => {
        fixVoteWrapperPositioning(table)
      }, 100) // Small delay to ensure DOM is fully rendered

      // Fix positioning on window resize
      window.addEventListener('resize', () => {
        // Check if still in mobile view
        if (window.matchMedia('(max-width: 575.98px)').matches) {
          fixVoteWrapperPositioning(table)
        }
      })
    }

    const buttons = table.querySelectorAll('.js-vote-btn')
    if (!buttons.length) return

    buttons.forEach((button) => {
      // If the user has already voted, disable all the buttons
      if (hasVoted) {
        button.setAttribute('disabled', 'disabled')
      }

      button.addEventListener('click', (e) => {
        e.preventDefault()

        // If the button is already clicked, disabled or the user has already voted, return
        if (button.classList.contains('btn-secondary--clicked') || button.disabled || hasVoted) return

        button.classList.add('btn-secondary--clicked')
        button.classList.add('btn-loading')

        const id = button.dataset.id
        const blockName = button.dataset.block_name
        const key = button.dataset.key

        const params = {
          id,
          blockName,
          key,
          anchor: block.dataset.anchor,
          action: 'block_best_player_vote',
          nonce: wp_params.ajax_nonce
        }

        // wp_params is passed by the enqueue_blocks_assets() function in the actions.php file
        fetch(`${wp_params.ajax_url}?${new URLSearchParams(params)}`)
          .then(async (response) => {
            response = await response.json()
            if (response.success) {
              const data = response.data
              const postData = data.data

              // Disable all buttons except the one voted
              buttons.forEach((button) => {
                if (Number(button.dataset.key) === postData.key) {
                  button.textContent = button.dataset.voted_text
                } else {
                  button.setAttribute('disabled', 'disabled')
                }
              })

              // Update the player score
              const scoreEl = table.querySelector(`.score-vote-wrapper__score[data-key="${postData.key}"]`)
              if (scoreEl) {
                scoreEl.textContent = postData.player_score
              }

              // Update row positions after score changes
              updateRowPositions(table)

              // Fix vote wrapper positioning after row update (only on mobile)
              if (window.matchMedia('(max-width: 575.98px)').matches) {
                setTimeout(() => {
                  fixVoteWrapperPositioning(table)
                }, 300) // Match the transition duration
              }

              // Update podium after row positions update
              updatePodium(block, table)

              // Save vote in localStorage DISABLED FOR TEST
              localStorage.setItem(
                postID,
                JSON.stringify({
                  blockBestPlayerVoted: true
                })
              )
            }
          })
          .catch((err) => {
            console.error(`${err} - Erreur ajax`)
          })
          .finally(() => {
            button.classList.remove('btn-loading')
          })
      })
    })
  })
}
